<template>
  <div
    class="relative xl:static w-full h-full"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div
      :id="'submenu-' + menus.id"
      class="relative flex items-center justify-between xl:justify-center font-bold cursor-pointer py-4 xl:py-8 pl-2 pr-5 h-full xl:border-white group hover:border-primary-850 border-b-2"
      :class="[
        localIsOpen
          ? 'border-b-2 border-primary-color xl:!border-primary-850 '
          : '    ',
      ]"
      @click="toggle"
    >
      <span
        class="text-lg font-medium"
        :class="[
          textColorClass,
          isActive ? 'text-primary-color font-bold' : '',
        ]"
      >
        {{ menus.title }}
      </span>

      <svg
        class="w-4 h-4 transition-transform duration-200 xl:hidden text-gray-dark"
        :class="{ 'rotate-180': localIsOpen }"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fill-rule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clip-rule="evenodd"
        />
      </svg>
    </div>

    <div
      v-if="localIsOpen"
      :class="[
        windowWidth >= xl
          ? 'xl:absolute xl:left-1/2 xl:transform xl:-translate-x-1/2 z-10 xl:w-[90%] mt-[1px] max-w-screen-xl rounded-lg shadow-md'
          : 'overflow-y-auto ',
      ]"
    >
      <div
        v-if="windowWidth >= xl"
        class="flex gap-6 bg-white rounded-b-lg shadow-inner"
      >
        <div
          class="w-1/3 p-10 h-full flex flex-col items-start pl-32 bg-white rounded-b-lg"
        >
          <div class="flex flex-col gap-2">
            <div v-for="(item, idx) in menus.subMenus" :key="idx" class="py-1">
              <inertia-link
                v-if="!item.subMenus && item.target !== '_blank'"
                :href="
                  item.route && $route().has(item.route)
                    ? $route(item.route)
                    : item.url
                "
                class="text-gray-800 text-md font-medium text-left flex items-center justify-start gap-4 hover:text-primary-color cursor-pointer"
                :class="checkCurrentPage(item) ? 'text-primary-color' : ''"
              >
                <img
                  :src="'/storage/images/icons/' + item.icon_class + '.svg'"
                  :alt="item.title"
                  class="w-6 h-6 flex-shrink-0"
                />
                {{ item.title }}
              </inertia-link>
              <a
                v-else
                :href="
                  item.route && $route().has(item.route)
                    ? $route(item.route)
                    : item.url
                "
                :target="item.target"
                rel="noreferrer"
                class="text-gray-800 text-md font-medium text-left flex items-center justify-start gap-4 hover:text-gray-900 cursor-pointer"
              >
                <img
                  :src="'/storage/images/icons/' + item.icon_class + '.svg'"
                  :alt="item.title"
                  class="w-6 h-6 flex-shrink-0"
                />
                {{ item.title }}
              </a>
            </div>
          </div>
        </div>

        <div
          class="flex justify-start items-start w-2/3 bg-primary-200 py-10 rounded-br-lg"
        >
          <div class="w-2/5 flex flex-col items-start pl-20">
            <h3
              class="mb-2 text-md font-bold text-primary-850 border-b border-primary-850 pb-3"
            >
              Related Topics
            </h3>
            <ul
              v-if="localIsOpen && relatedTopics"
              class="text-[#0E0E2C] text-left flex flex-col gap-4 pt-5"
            >
              <li
                v-for="(topic, index) in relatedTopics"
                :key="index"
                class="hover:text-gray-900"
              >
                <inertia-link
                  v-if="topic.title && !isHttps(topic.link)"
                  :href="topic.link"
                  class="block text-gray-dark text-md font-medium hover:text-primary-color"
                >
                  {{ topic.title }}
                </inertia-link>
                <a
                  v-if="topic.title && isHttps(topic.link)"
                  :href="topic.link"
                  class="block text-gray-dark text-md font-medium"
                  target="_blank"
                >
                  {{ topic.title }}
                </a>
              </li>
            </ul>
          </div>
          <div
            class="w-3/5 text-left border-l border-primary-850 px-16 flex flex-col items-start"
            v-if="infoData"
          >
            <h3
              class="mb-2 text-xl font-bold text-primary-850 border-b border-primary-850 pb-3"
            >
              {{ infoData.title }}
            </h3>
            <p class="text-gray-600 text-base mt-5">
              {{ infoData.description }}
            </p>
            <inertia-link
              v-if="!isHttps(infoData.link)"
              :href="infoData.link"
              class="mt-2"
            >
              <font-awesome-icon
                :icon="['fas', 'arrow-right']"
                size="1x"
                class="text-primary-850"
                :transform="'flip-v'"
              />
            </inertia-link>
            <a v-else :href="infoData.link" class="mt-2" target="_blank">
              <font-awesome-icon
                :icon="['fas', 'arrow-right']"
                size="1x"
                class="text-primary-850"
                :transform="'flip-v'"
              />
            </a>
          </div>
        </div>
      </div>

      <div v-else class="p-4">
        <div v-for="(item, idx) in menus.subMenus" :key="idx">
          <inertia-link
            v-if="!item.subMenus && item.target !== '_blank'"
            :href="
              item.route && $route().has(item.route)
                ? $route(item.route)
                : item.url
            "
            class="flex justify-between items-center py-2 cursor-pointer"
          >
            <div class="flex items-center gap-4">
              <img
                :src="'/storage/images/icons/' + item.icon_class + '.svg'"
                :alt="item.title"
                class="w-4 h-4"
              />

              <span class="font-semibold text-gray-dark text-left">
                {{ item.title }}
              </span>
            </div>
          </inertia-link>
          <a
            v-else
            :href="
              item.route && $route().has(item.route)
                ? $route(item.route)
                : item.url
            "
            :target="item.target"
            rel="noreferrer"
            class="flex justify-between items-center py-2 cursor-pointer"
          >
            <div class="flex items-center gap-4">
              <img
                :src="'/storage/images/icons/' + item.icon_class + '.svg'"
                :alt="item.title"
                class="w-4 h-4"
              />

              <span class="font-semibold text-gray-dark text-left">
                {{ item.title }}
              </span>
            </div>
            <svg
              class="w-4 h-4 transform transition-transform duration-200 -rotate-90 text-gray-dark"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </a>
        </div>
        <div
          class="flex flex-col gap-4 items-start mt-8 bg-primary-200 p-4 border-b-2 border-primary-850 text-left"
          v-if="infoData"
        >
          <h3
            class="mb-2 text-xl font-bold text-primary-850 border-b border-primary-850 pb-3 text-left"
          >
            {{ infoData.title }}
          </h3>
          <p class="text-gray-dark text-base text-left">
            {{ infoData.description }}
          </p>
          <inertia-link :href="infoData.link">
            <font-awesome-icon
              :icon="['fas', 'arrow-right']"
              size="1x"
              class="text-primary-850"
              :transform="'flip-v'"
            />
          </inertia-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const tailwindConfig = require("../../../../../tailwind.config.js");

export default {
  props: {
    menus: {
      type: Object,
      required: true,
    },
    transparent: {
      type: Boolean,
      default: false,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    hovered: {
      type: Boolean,
      default: false,
    },
    activeHover: {
      type: Boolean,
      default: false,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    relatedTopics() {
      return (this.menus.itemExtras ?? []).filter(
        (item) => item.category === "topics"
      );
    },
    infoData() {
      return (this.menus.itemExtras ?? []).find(
        (item) => item.category === "feature"
      );
    },
    textColorClass() {
      if (this.activeHover) {
        return this.hovered ? "text-primary-color" : "text-gray-light";
      } else {
        return "text-gray-dark";
      }
    },
  },

  data() {
    return {
      openSubmenu: null,
      xl: Number(tailwindConfig.theme.screens.xl.replace("px", "")),
      windowWidth: window.innerWidth,
      localIsOpen: this.isOpen,
      scrollOffset: 0,
    };
  },
  watch: {
    isOpen(newVal) {
      this.localIsOpen = newVal;
    },
    "$page.url": {
      immediate: true,
      handler() {
        if (this.isCurrentPage()) {
          this.$emit("activeMenu", this.menus.id);
        }
      },
    },
  },
  methods: {
    updateWindowSize() {
      this.windowWidth = window.innerWidth;
    },
    checkCurrentPage(menu) {
      if (!menu.route && !menu.subMenus) {
        return false;
      }
      let currentRoute = route().current().toLowerCase();

      let routeMatch =
        menu.route && currentRoute.includes(menu.route.toLowerCase());

      let subMenuMatch = false;
      if (menu.subMenus && menu.subMenus.length > 0) {
        subMenuMatch = menu.subMenus.some((sub) => {
          return sub.route && currentRoute.includes(sub.route.toLowerCase());
        });
      }

      let isCurrent =
        routeMatch ||
        currentRoute.includes(menu.title.toLowerCase()) ||
        subMenuMatch;

      return isCurrent;
    },
    onMouseEnter() {
      if (this.windowWidth >= this.xl) {
        this.localIsOpen = true;
      }
      this.$emit("hoverStart");
    },
    onMouseLeave() {
      if (this.windowWidth >= this.xl) {
        this.localIsOpen = false;
      }
      this.$emit("hoverEnd");
    },
    onClick() {
      if (this.windowWidth < this.xl) {
        this.localIsOpen = !this.localIsOpen;
      }
    },
    toggle() {
      this.localIsOpen = !this.localIsOpen;
      if (this.windowWidth < this.xl) {
        this.scrollToSubmenu();
      }
      this.$emit("toggle");
    },
    scrollToSubmenu() {
      this.$nextTick(() => {
        const submenu = document.getElementById(this.scrollElementId());
        submenu.scrollIntoView({ block: "start", behavior: "smooth" });
      });
    },
    scrollElementId() {
      return "submenu-" + this.menus.id;
    },
    isCurrentPage() {
      return this.checkCurrentPage(this.menus);
    },
    isHttps(url) {
      try {
        return new URL(url).protocol === "https:";
      } catch (e) {
        return false;
      }
    },
  },
  mounted() {
    this.updateWindowSize();
    window.addEventListener("resize", this.updateWindowSize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateWindowSize);
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
